import React from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { getDataPalette } from '../../../styles/dataColors.js';

const DonutChart = ({
  data = [],
  height = 300,
  innerRadius = 60,
  outerRadius = 100,
  dataKey = 'value',
  nameKey = 'name',
  colors = getDataPalette(),
  showTooltip = true,
  showLegend = true,
  legendAlign = 'center',
  legendVerticalAlign = 'bottom',
  legendLayout = 'horizontal',
  legendIconType = 'circle',
  legendItemGap = 16,
  legendFontSize = 12,
  legendTextColor = null,
  legendMarkerSize = 10,
  isAnimationActive = false,
  useEntryColor = false,
  emptyMessage = '',
  emptyColor = 'rgb(var(--app-border-rgb))',
  className = ''
}) => {
  const palette = colors && colors.length > 0 ? colors : getDataPalette();
  const hasData = data.some((entry) => Number(entry?.[dataKey]) > 0);
  const chartData = hasData
    ? data
    : [{ [dataKey]: 1, [nameKey]: emptyMessage || 'Sin datos' }];
  const legendJustify =
    legendAlign === 'left' ? 'flex-start' : legendAlign === 'right' ? 'flex-end' : 'center';

  const renderLegend = ({ payload }) => {
    if (!payload || payload.length === 0) return null;

    return (
      <ul
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: legendJustify,
          alignItems: 'center',
          gap: legendItemGap,
          listStyle: 'none',
          margin: 0,
          padding: 0
        }}
      >
        {payload.map((entry, index) => (
          <li
            key={`legend-item-${index}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              lineHeight: 1
            }}
          >
            <span
              style={{
                display: 'inline-block',
                width: legendMarkerSize,
                height: legendMarkerSize,
                borderRadius: legendIconType === 'circle' ? '50%' : 2,
                backgroundColor: entry.color
              }}
            />
            <span
              style={{
                color: legendTextColor || 'rgb(var(--app-text-muted-rgb))',
                fontSize: legendFontSize
              }}
            >
              {entry.value}
            </span>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className={className}>
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            dataKey={dataKey}
            nameKey={nameKey}
            isAnimationActive={isAnimationActive}
          >
            {chartData.map((entry, index) => {
              const paletteColor = palette[index % palette.length];
              const fillColor = !hasData
                ? emptyColor
                : useEntryColor
                ? (entry?.fill || entry?.color || paletteColor)
                : paletteColor;

              return <Cell key={`cell-${index}`} fill={fillColor} />;
            })}
          </Pie>
          {showTooltip && hasData ? (
            <Tooltip
              contentStyle={{
                backgroundColor: 'rgb(var(--app-surface-2-rgb))',
                border: '1px solid rgb(var(--app-border-rgb))',
                borderRadius: 8,
                color: 'rgb(var(--app-text-rgb))'
              }}
              labelStyle={{ color: 'rgb(var(--app-text-rgb))' }}
              itemStyle={{ color: 'rgb(var(--app-text-rgb))' }}
            />
          ) : null}
          {showLegend ? (
            <Legend
              align={legendAlign}
              verticalAlign={legendVerticalAlign}
              layout={legendLayout}
              iconType={legendIconType}
              content={hasData ? renderLegend : () => (
                emptyMessage ? (
                  <p style={{ margin: 0, textAlign: 'center', color: 'rgb(var(--app-text-muted-rgb))', fontSize: legendFontSize }}>
                    {emptyMessage}
                  </p>
                ) : null
              )}
            />
          ) : null}
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default DonutChart;
